import { useEffect, useState } from "react";
import { GetArticlesByCategory, GetCategories, GetLatestStaffPick, GetLatestArticles } from "../../../Api/Article";
import ArticleDTO from "../../../Models/DTOs/ArticleDTO";
import ArticleCategoryDTO from "../../../Models/DTOs/ArticleCategoryDTO";
import IArticleCategoryListDTO from "../../../Models/DTOs/IArticleCategoryListDTO";

export function distinctArticles(articles: ArticleDTO[], excludedIds: number[] = []) {
    const seen = new Set(excludedIds);
    return articles.filter(article => {
        if (seen.has(article.id)) return false;
        seen.add(article.id);
        return true;
    });
}

export default function useFetchArticleFeed() {
    const [categories, setCategories] = useState<ArticleCategoryDTO[]>([]);
    const [featuredArticle, setFeaturedArticle] = useState<ArticleDTO | null>(null);
    const [featuredIsStaffPick, setFeaturedIsStaffPick] = useState(false);
    const [articleGroups, setArticleGroups] = useState<IArticleCategoryListDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [revision, setRevision] = useState(0);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError("");
        setArticleGroups([]);
        setFeaturedArticle(null);
        setFeaturedIsStaffPick(false);

        async function load() {
            const [latestResult, pickResult, categoriesResult] = await Promise.allSettled([
                GetLatestArticles(6, 0, controller),
                GetLatestStaffPick(controller),
                GetCategories(controller),
            ]);
            if (controller.signal.aborted) return;

            const latest = latestResult.status === "fulfilled" ? latestResult.value.items : [];
            const pick = pickResult.status === "fulfilled" ? pickResult.value.items[0] : undefined;
            const lead = pick ?? latest[0] ?? null;
            const recent = distinctArticles(latest, lead ? [lead.id] : []).slice(0, 5);
            const groups: IArticleCategoryListDTO[] = recent.length
                ? [{ Id: -1, Title: "Latest articles", Articles: recent }]
                : [];
            const topics = categoriesResult.status === "fulfilled" ? categoriesResult.value.items : [];
            const initialFailed = [latestResult, pickResult, categoriesResult].some(result => result.status === "rejected");

            setCategories(topics);
            setFeaturedArticle(lead);
            setFeaturedIsStaffPick(!!pick);
            setArticleGroups(groups);
            if (initialFailed) setError("Some articles or topics could not be loaded. Please try again.");

            // Each topic has its own newest pick. Do not exclude stories shown in
            // other feeds: doing so could select an older pick or skip recent news.
            const sections = await Promise.all(topics.map(async category => {
                const [articlesResult, categoryPickResult] = await Promise.allSettled([
                    GetArticlesByCategory(category.id, 0, 4, controller),
                    GetLatestStaffPick(controller, category.id),
                ]);
                const articles = articlesResult.status === "fulfilled" ? articlesResult.value.items : [];
                const categoryPick = categoryPickResult.status === "fulfilled" ? categoryPickResult.value.items[0] : undefined;
                const selection = distinctArticles([...(categoryPick ? [categoryPick] : []), ...articles]).slice(0, 4);
                return {
                    group: { Id: category.id, Title: category.title, Articles: selection,
                        PickArticleId: categoryPick?.id } as IArticleCategoryListDTO,
                    failed: articlesResult.status === "rejected" || categoryPickResult.status === "rejected",
                };
            }));
            if (controller.signal.aborted) return;
            setArticleGroups([...groups, ...sections.filter(section => section.group.Articles.length).map(section => section.group)]);
            setError(initialFailed || sections.some(section => section.failed)
                ? "Some articles or topics could not be loaded. Please try again." : "");
            setLoading(false);
        }

        void load();
        return () => controller.abort();
    }, [revision]);

    return { categories, featuredArticle, featuredIsStaffPick, articleGroups, loading, error, retry: () => setRevision(value => value + 1) };
}